import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Our Services', path: '/services' },
  { label: 'Projects', path: '/projects' },
  { label: 'Our Team', path: '/team' },
  { label: 'Contact Us', path: '/contact' }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img
              src="/logo.png"
              alt="Alakhe Conglomerate"
              className="h-12 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="flex items-center space-x-2" style={{ display: 'none' }}>
              <div className="w-10 h-10 rounded-full border-4 border-charcoal flex items-center justify-center">
                <div className="w-4 h-4 bg-charcoal rounded-full"></div>
              </div>
              <div>
                <h1 className="text-brand-red font-bold text-lg leading-tight">ALAKHE</h1>
                <p className="text-charcoal text-xs font-semibold tracking-wider">CONGLOMERATE</p>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={({ isActive }) =>
                  `relative font-medium text-sm tracking-wide transition-colors pb-1 ${
                    isActive
                      ? 'text-brand-red after:absolute after:left-0 after:bottom-0 after:w-full after:h-0.5 after:bg-brand-red'
                      : 'text-charcoal hover:text-brand-red'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Button
              onClick={() => navigate('/contact')}
              data-testid="header-get-quote-button"
              className="bg-brand-red hover:bg-red-700 text-white font-semibold px-6 rounded-md shadow-sm hover:shadow-md transition-all"
            >
              Get Quote
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-charcoal hover:text-brand-red transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-6 border-t border-gray-200" data-testid="mobile-nav">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `font-medium text-left transition-colors ${
                      isActive ? 'text-brand-red' : 'text-charcoal hover:text-brand-red'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/contact');
                }}
                data-testid="mobile-get-quote-button"
                className="bg-brand-red hover:bg-red-700 text-white font-semibold w-full rounded-md"
              >
                Get Quote
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
